import { WidgetProps } from "@rjsf/core";
import { useContactTypes } from "components/utils/useContactTypes";
import { isEmpty, toNumber } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ContactInboundData, ContactValidationResult } from "../ApplicationContactsWidget/types";
import { ContactType, isContactDataEmpty } from "components/utils/contacts";
import { refreshApplicationContacts, useApplicationContacts } from "../ApplicationContactsWidget/useApplicationContacts";
import {
    createContactFormDataObject,
    getContactTypeByName,
    validateContact,
    validateContactDataFormat,
} from "../ApplicationContactsWidget/utils";
import { AdditionalContactForm } from "./AdditionalContactForm";
import { AdditionalContactFormData, AdditionalContactRequirements } from "./types";
import { useAdditionalContacts } from "./useAdditionalContacts";
import { isContractorContact } from "./utils";
import { useApplicationContactRequirements } from "../ApplicationContactsWidget/useApplicationContactRequirements";

export const AdditionalContactsWidget = (props: WidgetProps) => {
    const { applicationNumber, additionalContactsWidgetRef, onAdditionalContactsLoaded } = props.formContext ?? {};
    const [additionalContacts, isLoadingAdditionalContacts] = useAdditionalContacts(applicationNumber);
    const [contacts, isLoadingContacts] = useApplicationContacts(applicationNumber);
    const [contactTypes = [], isLoadingContactTypes] = useContactTypes();
    const [requirements] = useApplicationContactRequirements(applicationNumber);

    const [contactFormsData, setContactFormsData] = useState<AdditionalContactFormData[]>([]);
    const [contactErrors, setContactErrors] = useState<ContactValidationResult[]>([]);

    const contactFormsReady =
        !isLoadingAdditionalContacts &&
        !isLoadingContacts &&
        !isLoadingContactTypes &&
        contactFormsData.length === additionalContacts.length;

    useEffect(() => {
        if (additionalContactsWidgetRef) {
            additionalContactsWidgetRef.current = {
                validate: () => {
                    const errors = additionalContacts.map((item, index) =>
                        validateAdditionalContactData({
                            contactData: contactFormsData[index] ?? {},
                            requirements: item,
                            disableContactValidation: requirements.disableAddlContactValidation ?? false,
                        })
                    );
                    setContactErrors(errors);
                    return !errors.every(isEmpty);
                },
                validateDataFormat: () => {
                    const errors = contactFormsData.map((data) => validateContactDataFormat(data ?? {}));
                    setContactErrors(errors);
                    return !errors.every(isEmpty);
                },
                getSubmitValues: () => {
                    const foundContacts: string[] = [];
                    return additionalContacts
                        .map((req, index) => {
                            const formData = contactFormsData[index] ?? {};
                            const contact = contacts?.find(
                                (c) => c.contactTitle === req.title && !foundContacts.includes(c.contactNumber as string)
                            );
                            foundContacts.push(contact?.contactNumber ?? "");
                            const contactType = getContactTypeByName(contactTypes, ContactType.Other) as number;
                            const workflowTargetGroupId =
                                contact?.workflowTargetGroupId ??
                                (req.workflowTargetGroupId ? Number(req.workflowTargetGroupId) : undefined);

                            if (Object.values(formData).every(isEmpty) && !contact?.contactNumber) {
                                return undefined;
                            }

                            return {
                                ...formData,
                                contactNumber: contact?.contactNumber,
                                workflowTargetGroupId,
                                applicationContactType: ContactType.Other,
                                contactType,
                                contactTitle: req.title,
                                ...(isContractorContact(formData) ? { origId: toNumber(formData.origId) } : {}),
                            };
                        })
                        .filter((c) => c);
                },
                fieldNumber: props.uiSchema["af:fieldNumber"],
            };
        }
        return () => {
            if (additionalContactsWidgetRef?.current) {
                additionalContactsWidgetRef.current = undefined;
            }
        };
    }, [
        additionalContactsWidgetRef,
        props.uiSchema,
        additionalContacts,
        contacts,
        contactTypes,
        contactFormsData,
        requirements.disableAddlContactValidation,
    ]);

    useEffect(() => {
        if (contacts && additionalContacts) {
            setContactFormsData(() => {
                const foundContacts: string[] = [];
                return additionalContacts.map((req) => {
                    const contact = contacts.find(
                        (c) => c.contactTitle === req.title && !foundContacts.includes(c.contactNumber as string)
                    );
                    foundContacts.push(contact?.contactNumber ?? "");
                    return {
                        ...(createContactFormDataObject(contact) ?? {}),
                        origId: contact?.origId,
                    };
                });
            });
        }
    }, [additionalContacts, contacts]);

    useEffect(() => {
        if (contactFormsReady) {
            onAdditionalContactsLoaded?.();
        }
    }, [contactFormsReady, onAdditionalContactsLoaded]);

    useEffect(() => {
        return () => {
            refreshApplicationContacts(applicationNumber);
        };
    }, [applicationNumber]);

    const onUseCustomerContact = useCallback(
        (selectedContact: ContactInboundData | undefined, index: number) => {
            if (!selectedContact) return;
            const formData = createContactFormDataObject(selectedContact);
            if (!formData) return;

            setContactFormsData((prev) =>
                prev.map((c, i) => (i === index ? { ...formData, storedContactNumber: selectedContact.contactNumber } : c))
            );

            const errors = validateAdditionalContactData({
                contactData: formData,
                requirements: additionalContacts[index],
                disableContactValidation: !!requirements.disableAddlContactValidation,
            });
            setContactErrors((prev) => {
                const next = [...prev];
                next[index] = errors;
                return next;
            });
        },
        [additionalContacts, requirements.disableAddlContactValidation]
    );

    const onChange = useCallback((formData: AdditionalContactFormData, index: number) => {
        setContactFormsData((prev) => prev.map((c, i) => (i === index ? formData : c)));
    }, []);

    const onClearAdditionalContact = useCallback((index: number) => {
        setContactFormsData((prev) => prev.map((c, i) => (i === index ? {} : c)));
    }, []);

    const validateAdditionalContactData = ({
        contactData,
        requirements,
        disableContactValidation,
    }: {
        contactData: AdditionalContactFormData;
        requirements: AdditionalContactRequirements;
        disableContactValidation: boolean;
    }): ContactValidationResult => {
        if (isContractorContact(contactData) || (disableContactValidation && isContactDataEmpty(contactData))) {
            return undefined;
        }

        const requiredFields = requirements.requiredFields?.split(",").map((f) => f.trim().toLowerCase()) ?? [
            "firstname",
            "lastname",
            "company",
        ];
        const showFields = requirements.showFieldNames?.split(",").map((f) => f.trim().toLowerCase()) ?? [];
        const visibleRequiredFields = requiredFields.filter((f) => showFields.includes(f));

        return validateContact(visibleRequiredFields, contactData);
    };

    if (!contactFormsReady || props.readonly) {
        return null;
    }

    return (
        <div className="additional-contacts-widget">
            {additionalContacts.map((item, index) => (
                <AdditionalContactForm
                    key={index}
                    index={index}
                    formData={contactFormsData[index]}
                    requirements={item}
                    fullContactRequirements={requirements}
                    errors={contactErrors[index]}
                    applicationNumber={applicationNumber}
                    onChange={(form) => onChange(form, index)}
                    onClearForm={() => onClearAdditionalContact(index)}
                    onUseCustomerContact={(contact) => onUseCustomerContact(contact, index)}
                />
            ))}
        </div>
    );
};

export default AdditionalContactsWidget;
